import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TimeLimitContext from '../../context/TimeLimitContext';
import StealCapContext from '../../context/StealCapContext';
import { ToastContainer, toast } from 'react-toastify';
import { selectRandomCombination } from '../../helpers/selectRandomCombination';
import { getSquareCombinations, getSquareCombinationsArg } from '../../services/api';
import { winningPositions } from '../../helpers/winningPositions';
import { checkPlayer } from '../../helpers/checkPlayer';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../../components/Navbar/Navbar';
import AdBanner from '../../components/AdBanner/AdBanner';
import Board from '../../components/Board/Board';
import ScoreBoard from '../../components/ScoreBoard/ScoreBoard';
import NextButton from '../../components/NextButton/NextButton';
import PlayerSelected from '../../components/PlayerSelected/PlayerSelected';
import CountdownTimer from '../../components/CountdownTimer/CountdownTimer';
import '../../App.css';

const LocalGamePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [turn, setTurn] = useState('X');
    const [squares, setSquares] = useState(Array(9).fill({ value: '', playerName: '', claims: 0, claimedBy: [], shielded: false }));
    const [winningSquares, setWinningSquares] = useState([]);
    const [score, setScore] = useState({
        X: 0,
        O: 0,
    });
    const [league, setLeague] = useState('normal');
    const [items, setItems] = useState(Array(6).fill({}));
    const [combos, setCombos] = useState([]);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const { timeLimit } = useContext(TimeLimitContext);
    const [timeRemaining, setTimeRemaining] = useState(timeLimit);
    const variant = location.state?.variant || 'classic';
    const isStealMode = variant === 'steal';
    const isFour12Mode = variant === 'four12';
    const { stealCap } = useContext(StealCapContext);
    const [consecutiveSkips, setConsecutiveSkips] = useState(0);
    const [stealFlashIdx, setStealFlashIdx] = useState(null);
    // Blindaje: una vez por partida, cada jugador puede elegir una casilla que, al jugarla
    // correctamente, queda intocable para siempre (sin importar el tope de jugadas).
    const [shieldUsed, setShieldUsed] = useState({ X: false, O: false });
    const [armedShieldPlayer, setArmedShieldPlayer] = useState(null); // 'X' | 'O' | null
    // Modo 412: casilla "elegida" este turno y set de índices de `items` (0-5) ya revelados —
    // ver SoloBotPage.js para la explicación completa del patrón.
    const [lockedSquare, setLockedSquare] = useState(null);
    const [revealedItems, setRevealedItems] = useState(() => new Set());
    const itemMaping = {
        0: [items[0], items[3]],
        1: [items[1], items[3]],
        2: [items[2], items[3]],
        3: [items[0], items[4]],
        4: [items[1], items[4]],
        5: [items[2], items[4]],
        6: [items[0], items[5]],
        7: [items[1], items[5]],
        8: [items[2], items[5]],
    };
    const squareToItemIndices = (square) => [square % 3, 3 + Math.floor(square / 3)];

    useEffect(() => {
        setTimeRemaining(timeLimit);

        const timer = setInterval(() => {
            setTimeRemaining((prevTimeRemaining) => {
                if (prevTimeRemaining === 0) {
                    handleSkipTurn();
                    setSelectedPlayer(null);
                    return timeLimit;
                }
                return prevTimeRemaining - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLimit, turn]);

    useEffect(() => {
        if (location.state && location.state.mode) {
            if (location.state.mode === 'normal') {
                setLeague('normal');
                getSquareCombinations()
                    .then((data) => {
                        setCombos(data);
                        const initialCombination = selectRandomCombination(data);
                        setItems(initialCombination);
                    })
                    .catch((err) => console.error("Error al obtener combinaciones:", err));
            } else if (location.state.mode === 'arg') {
                setLeague('arg');
                getSquareCombinationsArg()
                    .then((data) => {
                        setCombos(data);
                        const initialCombination = selectRandomCombination(data);
                        setItems(initialCombination);
                    })
                    .catch((err) => console.error("Error al obtener combinaciones:", err));
            } else {
                return;
            }
        }
    }, [location.state]);

    const handleSkipTurn = () => {
        setLockedSquare(null);
        setTurn((prevTurn) => (prevTurn === 'X' ? 'O' : 'X'));
    }

    const handleChangeItems = useCallback(() => {
        if (combos.length > 0) {
            const newCombination = selectRandomCombination(combos);
            setItems(newCombination);
        }
    }, [combos]);

    const handlePlayerSelect = (player) => {
        if (isFour12Mode && lockedSquare !== null) {
            attemptPlacement(lockedSquare, player);
            return;
        }
        setSelectedPlayer(player);
    }

    // Extraído de handleClick para poder dispararlo tanto desde un click (Clásico/Robo) como
    // desde elegir un jugador en el buscador (Modo 412, donde la casilla ya está comprometida).
    const attemptPlacement = (square, playerToPlace) => {
        const sq = squares[square];
        const [item1, item2] = itemMaping[square];
        const playedInItem1 = checkPlayer(playerToPlace, item1);
        const playedInItem2 = checkPlayer(playerToPlace, item2);

        if (playedInItem1 && playedInItem2) {
            const wasSteal = isStealMode && sq.value !== '' && sq.value !== turn;
            const wasReinforce = isStealMode && sq.value !== '' && sq.value === turn;
            const stolenFromName = sq.playerName;
            const newClaims = (sq.claims || 0) + 1;
            // Reforzar tu propia casilla (poner otro jugador sobre una que ya era tuya) la
            // blinda para siempre al instante, GRATIS: no gasta el blindaje único del botón.
            const willShield = wasReinforce || (isStealMode && armedShieldPlayer === turn);
            const consumesShieldCharge = willShield && !wasReinforce;
            let newSquares = [...squares];
            newSquares.splice(square, 1, {
                value: turn,
                playerName: playerToPlace.nombre,
                playerImg: playerToPlace.img || null,
                claims: newClaims,
                claimedBy: isStealMode ? [...(sq.claimedBy || []), playerToPlace._id] : (sq.claimedBy || []),
                shielded: willShield ? true : (sq.shielded || false),
            });
            setConsecutiveSkips(0);
            checkForWinner(newSquares);
            setSquares(newSquares);
            setSelectedPlayer(null);
            if (isFour12Mode) setLockedSquare(null);
            if (consumesShieldCharge) {
                setShieldUsed(prev => ({ ...prev, [turn]: true }));
                setArmedShieldPlayer(null);
                if (wasSteal) {
                    setStealFlashIdx(square);
                    setTimeout(() => setStealFlashIdx(null), 1200);
                }
                toast.success(
                    wasSteal
                        ? `🛡️🔥 ¡Le robaste la casilla a ${stolenFromName} y la blindaste para siempre!`
                        : '🛡️ ¡Casilla blindada para siempre! Nadie va a poder tocarla.',
                    {
                        position: "top-right", autoClose: 4000, hideProgressBar: false,
                        closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
                    }
                );
            } else if (wasReinforce) {
                toast.success('🛡️ ¡Reforzaste tu casilla y quedó blindada para siempre! (No gastó tu blindaje).', {
                    position: "top-right", autoClose: 4000, hideProgressBar: false,
                    closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
                });
            } else if (wasSteal) {
                setStealFlashIdx(square);
                setTimeout(() => setStealFlashIdx(null), 1200);
                toast.success(`🔥 ¡Le robaste la casilla a ${stolenFromName}!`, {
                    position: "top-right", autoClose: 3000, hideProgressBar: false,
                    closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
                });
            }
        } else if (isFour12Mode) {
            // Igual que Clásico/Robo: errar pierde el turno. handleSkipTurn() ya limpia
            // lockedSquare, así que en el próximo turno se puede elegir cualquier casilla
            // vacía de nuevo — no queda "trabado" reintentando la misma para siempre.
            toast.warn('Ese jugador no cumple los 2 requisitos de esta casilla. 😔 Perdiste el turno.', {
                position: "top-right", autoClose: 4000, hideProgressBar: false,
                closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
            });
            setConsecutiveSkips(prev => prev + 1);
            handleSkipTurn();
            setSelectedPlayer(null);
        } else {
            toast.warn('The player does not meet the requirements. 😔You lost the turn.', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "dark",
            });
            setConsecutiveSkips(prev => prev + 1);
            handleSkipTurn();
            setSelectedPlayer(null);
        }
    }

    const handleClick = (square) => {
        const sq = squares[square];

        if (isFour12Mode) {
            if (sq.value !== '') return; // sin robo en 412: ocupada, Square ya bloquea el click
            if (lockedSquare !== null && lockedSquare !== square) {
                toast.info('Primero resolvé la casilla que elegiste (o usá Skip Turn).', {
                    position: "top-right", autoClose: 3000, hideProgressBar: false,
                    closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
                });
                return;
            }
            if (lockedSquare === null) {
                setLockedSquare(square);
                const [colIdx, rowIdx] = squareToItemIndices(square);
                setRevealedItems(prev => new Set(prev).add(colIdx).add(rowIdx));
                return;
            }
            // lockedSquare === square: ya está revelada, reintentar con el jugador elegido.
            if (selectedPlayer) attemptPlacement(square, selectedPlayer);
            return;
        }

        if (!selectedPlayer) {
            toast.error('Select a player first', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "dark",
            });
            return;
        }

        if (isStealMode && sq.value !== '') {
            if (sq.shielded) {
                toast.info('🛡️ Esta casilla está blindada para siempre. Elegí otra.', {
                    position: "top-right", autoClose: 4000, hideProgressBar: false,
                    closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
                });
                return; // no pierde el turno, solo avisa
            }
            if (stealCap !== 'unlimited' && (sq.claims || 0) >= stealCap) {
                toast.info(`Esta casilla ya llegó al tope de ${stealCap} jugadas y quedó bloqueada. Elegí otra.`, {
                    position: "top-right", autoClose: 4000, hideProgressBar: false,
                    closeOnClick: true, pauseOnHover: true, draggable: true, theme: "dark",
                });
                return; // no pierde el turno, solo avisa
            }
        } else if (!isStealMode && sq.value !== '') {
            return; // clásico: Square ya bloquea el click, resguardo
        }

        if (isStealMode && sq.value !== '' && (sq.claimedBy || []).includes(selectedPlayer._id)) {
            toast.warn('Ese jugador ya se usó en ESTA casilla. Elegí otro (podés usarlo en otra casilla).', {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "dark",
            });
            setSelectedPlayer(null); // no pierde el turno, solo tiene que elegir otro jugador
            return;
        }

        attemptPlacement(square, selectedPlayer);
    }

    const checkForWinner = newSquares => {
        for (let i = 0; i < winningPositions.length; i++) {
            const [a, b, c] = winningPositions[i];
            if (newSquares[a] && newSquares[a].value !== '' && newSquares[a].value === newSquares[b].value && newSquares[a].value === newSquares[c].value) {
                endGame(newSquares[a].value, winningPositions[i])
                return
            }
        }

        // Clásico: tablero lleno = empate. Robo: las casillas ocupadas siguen siendo robables,
        // así que el tablero lleno NO implica empate (la salvaguarda de partidas eternas es
        // aparte, ver el useEffect de consecutiveSkips más abajo).
        if (!isStealMode && !newSquares.some(square => square.value === '')) {
            endGame(null, Array.from(Array(10).keys()));
            return;
        }
        handleSkipTurn();
    }

    // Robo y 412: si nadie pudo jugar en 2 vueltas completas (4 turnos salteados seguidos sin
    // ninguna jugada válida), se corta como empate en vez de trabarse para siempre.
    useEffect(() => {
        if ((isStealMode || isFour12Mode) && turn !== null && consecutiveSkips >= 4) {
            endGame(null, Array.from(Array(10).keys()));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consecutiveSkips, isStealMode, isFour12Mode, turn]);

    const endGame = (result, winningPositions) => {
        setTurn(null);
        if (result != null) {
            setScore({
                ...score,
                [result]: score[result] + 1,
            })
        }
        setWinningSquares(winningPositions);
        setTimeout(reset, 3000);
    }

    const reset = () => {
        const newTurn = turn === 'X' ? 'O' : 'X';
        setSquares(Array(9).fill({ value: '', playerName: '', claims: 0, claimedBy: [], shielded: false }));
        setTurn(newTurn);
        setWinningSquares([]);
        setTimeRemaining(timeLimit);
        setConsecutiveSkips(0);
        setShieldUsed({ X: false, O: false });
        setArmedShieldPlayer(null);
        setLockedSquare(null);
        setRevealedItems(new Set());
        handleChangeItems();
    }

    // eslint-disable-next-line no-unused-vars
    const handleClickTitle = () => {
        navigate('/')
    }

    return (
        <div className="container">
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
            />
            <Navbar gamePage />

            <div className="game-page">
            <h1 className="game-title">Footy <span>TicTacToe</span></h1>
            <div className="game-layout">
              <aside className="game-panel game-panel--left">
                <ScoreBoard scoreO={score.O} scoreX={score.X} turn={turn} />
                {timeRemaining !== null && (
                  <div className="panel-card panel-card--timer">
                    <span className="sidebar-label">Time</span>
                    <CountdownTimer timeRemaining={timeRemaining} />
                  </div>
                )}
                {selectedPlayer && <PlayerSelected player={selectedPlayer} />}
              </aside>
              <main className="game-center">
                <Board winningSquares={winningSquares} turn={turn} squares={squares} onClick={handleClick} clubSquares={items} handlePlayerSelect={handlePlayerSelect} player={turn} playerFootball={selectedPlayer} league={league} isStealMode={isStealMode} stealCap={stealCap} stealFlashSquare={stealFlashIdx} isFour12Mode={isFour12Mode} lockedSquare={lockedSquare} revealedItems={revealedItems} />
              </main>
              <aside className="game-panel game-panel--right">
                {isStealMode && turn !== null && (
                    <button
                        className={`btn-game btn-shield${armedShieldPlayer === turn ? ' btn-shield--armed' : ''}`}
                        disabled={shieldUsed[turn]}
                        onClick={() => setArmedShieldPlayer(prev => prev === turn ? null : turn)}
                    >
                        {shieldUsed[turn]
                            ? '🛡️ Blindaje ya usado'
                            : armedShieldPlayer === turn
                                ? '🛡️ Blindaje armado (cancelar)'
                                : '🛡️ Blindar próxima jugada'}
                    </button>
                )}
                <NextButton onClick={reset} changeTurn={handleSkipTurn} turn={turn} />
              </aside>
            </div>

            {/* Anuncios: debajo del área de juego y, en pantallas muy anchas, columnas a los
                costados. Todos separados del tablero (ver AdBanner). */}
            <AdBanner placement="game" />
            <AdBanner placement="rail-left" />
            <AdBanner placement="rail-right" />
            </div> {/* game-page */}
        </div>
    );
}

export default LocalGamePage;